import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 盛世娱乐`,
    description: category.description,
    url: `https://1616e.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '盛世娱乐官方平台',
      url: 'https://1616e.cn',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://1616e.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `https://1616e.cn/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 盛世娱乐 | 畅享${category.name}极致体验`}
        description={category.description}
        keywords={`${category.name},盛世娱乐${category.name},${category.name}平台,盛世娱乐`}
        canonicalUrl={`https://1616e.cn/category/${slug}`}
        ogTitle={`${category.name} - 盛世娱乐官方平台`}
        ogDescription={category.description}
      />
      <JsonLd data={webPageSchema} />
      <JsonLd data={breadcrumbSchema} />

      <main className="pt-16">
        {/* Category Header */}
        <section className="relative w-full h-[320px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-primary-500/90 mx-auto mb-4">
              <i className={`${category.icon} text-background-50 text-xl md:text-2xl`}></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-200 max-w-xl mx-auto">
              {category.description}
            </p>
          </div>
        </section>

        {/* Breadcrumb & Content */}
        <section className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />

            <div className="py-8">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-500">{article.date}</span>
                          <span className="text-xs text-foreground-500">·</span>
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h2 className="text-sm font-bold font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {article.title}
                        </h2>
                        <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2.5 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                    <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                  <Link
                    to="/"
                    className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-16 bg-background-100/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-8 text-center">
              探索更多娱乐项目
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70">
                      <i className={`${cat.icon} text-primary-500 text-base`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}